/**
 * Link targets and rich text used by the supplier 360 narrative copy. The
 * copy lives in plain .ts files, so links are described as data and turned
 * into typed router links by supplier360-rich-text.tsx.
 */
import type {
  ContractId,
  MaterialId,
  SupplierId,
} from '@/demo/procurement/data/types';

export type LinkTarget =
  | { kind: 'invoice'; invoiceId: string }
  | { kind: 'alert'; alertId: string }
  | { kind: 'contract'; contractId: ContractId; section?: string }
  | { kind: 'material'; materialId: MaterialId }
  | { kind: 'supplier'; supplierId: SupplierId }
  | { kind: 'sourcing'; eventId: string }
  | { kind: 'onboarding'; caseId: string }
  | { kind: 'requests'; requestId: string };

export type RichSegment = string | { text: string; link: LinkTarget };

/** A sentence made of plain text and links. */
export type RichText = RichSegment[];

export function invoiceLink(invoiceId: string, text = invoiceId): RichSegment {
  return { text, link: { kind: 'invoice', invoiceId } };
}

export function alertLink(alertId: string): RichSegment {
  return { text: alertId, link: { kind: 'alert', alertId } };
}

export function contractLink(
  contractId: ContractId,
  section?: string,
  text?: string,
): RichSegment {
  return {
    text: text ?? (section ? `§${section}` : contractId),
    link: { kind: 'contract', contractId, section },
  };
}

export function materialLink(
  materialId: MaterialId,
  text: string,
): RichSegment {
  return { text, link: { kind: 'material', materialId } };
}

export function supplierLink(
  supplierId: SupplierId,
  text: string,
): RichSegment {
  return { text, link: { kind: 'supplier', supplierId } };
}
